import React, { createContext, useContext, useState, useEffect, useMemo, PropsWithChildren } from 'react';
import { moduleClientFactory } from '../../api-service/module-client-factory';
import { useCommonCitizenContext } from './citizen-context-hooks';

interface AnonymousOverrideState {
  canOverride: boolean;
  isOverrideActive: boolean;
  toggleOverride: () => void;
}

const defaultState: AnonymousOverrideState = {
  canOverride: false,
  isOverrideActive: false,
  toggleOverride: () => {},
};

const AnonymousOverrideCtx = createContext<AnonymousOverrideState>(defaultState);

export function AnonymousOverrideProvider({ children }: PropsWithChildren) {
  const { isAnonymous } = useCommonCitizenContext();
  const [isSchultzUser, setIsSchultzUser] = useState(false);
  const [isOverrideActive, setIsOverrideActive] = useState(false);

  useEffect(() => {
    if (!isAnonymous) return;

    let cancelled = false;
    const citizenClient = moduleClientFactory.createCitizenClient();

    citizenClient.isCurrentUserSchultzUser().then((response) => {
      if (!cancelled) {
        setIsSchultzUser(response.isSchultzUser);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isAnonymous]);

  const value = useMemo<AnonymousOverrideState>(
    () => ({
      canOverride: (isAnonymous ?? false) && isSchultzUser,
      isOverrideActive,
      toggleOverride: () => setIsOverrideActive((prev) => !prev),
    }),
    [isAnonymous, isSchultzUser, isOverrideActive]
  );

  return <AnonymousOverrideCtx.Provider value={value}>{children}</AnonymousOverrideCtx.Provider>;
}

export function useAnonymousOverride(): AnonymousOverrideState {
  return useContext(AnonymousOverrideCtx);
}

export function useIsAnonymousCitizen(): boolean {
  const { isAnonymous } = useCommonCitizenContext();
  const { isOverrideActive } = useAnonymousOverride();

  if (isOverrideActive) return false;
  return isAnonymous ?? false;
}
